import { useEffect, useMemo, useRef } from "react";
import type { MetricRow, ReplayPayload } from "../types";

export interface MetricsViewProps {
  replay: ReplayPayload;
  tick: number;
}

const MAX_SERIES = 12; // like saga report
const CHART_COLORS = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b",
  "#e377c2", "#7f7f7f", "#bcbd22", "#17becf", "#aec7e8", "#98df8a",
];

type Series = Map<string, [number, number][]>;

function groupRows(rows: MetricRow[]): Map<string, Series> {
  const metrics = new Map<string, Series>();
  for (const row of rows) {
    const key = row.key === null ? "value" : String(row.key);
    let series = metrics.get(row.metric);
    if (!series) {
      series = new Map();
      metrics.set(row.metric, series);
    }
    let points = series.get(key);
    if (!points) {
      points = [];
      series.set(key, points);
    }
    points.push([row.tick, row.value]);
  }
  return metrics;
}

export function MetricsView({ replay, tick }: MetricsViewProps) {
  const metrics = useMemo(() => groupRows(replay.metrics), [replay.metrics]);
  if (metrics.size === 0) {
    return (
      <div className="view">
        <div className="pane-empty">this definition declares no metrics</div>
      </div>
    );
  }
  return (
    <div className="view">
      <div className="metrics-grid">
        {[...metrics.entries()].map(([name, series]) => (
          <MetricChart
            key={name}
            name={name}
            series={series}
            tick={tick}
            maxTick={replay.manifest.ticks}
          />
        ))}
      </div>
    </div>
  );
}

function MetricChart(props: { name: string; series: Series; tick: number; maxTick: number }) {
  const { name, series, tick, maxTick } = props;
  const canvas = useRef<HTMLCanvasElement>(null);
  const kept = useMemo(() => [...series.entries()].slice(0, MAX_SERIES), [series]);
  const dropped = series.size - kept.length;

  useEffect(() => {
    const element = canvas.current;
    const ctx = element?.getContext("2d");
    if (!element || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    // element.clientWidth is the CSS width source (fallback 640).
    const cssWidth = element.clientWidth || 640;
    const cssHeight = 160;
    element.width = cssWidth * dpr;
    element.height = cssHeight * dpr;
    element.style.width = `${cssWidth}px`;
    element.style.height = `${cssHeight}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // All drawing coordinates in CSS pixels.
    const width = cssWidth;
    const height = cssHeight;
    const pad = { left: 46, right: 8, top: 8, bottom: 18 };

    let min = Infinity;
    let max = -Infinity;
    for (const [, points] of kept) {
      for (const [, value] of points) {
        if (value < min) min = value;
        if (value > max) max = value;
      }
    }
    if (!isFinite(min)) return;
    if (min === max) {
      min -= 0.5;
      max += 0.5;
    }
    const xOf = (t: number) =>
      pad.left + ((t - 1) / Math.max(1, maxTick - 1)) * (width - pad.left - pad.right);
    const yOf = (v: number) =>
      height - pad.bottom - ((v - min) / (max - min)) * (height - pad.top - pad.bottom);

    ctx.fillStyle = "#12161f";
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = "#232a38";
    ctx.strokeRect(pad.left, pad.top, width - pad.left - pad.right, height - pad.top - pad.bottom);
    ctx.fillStyle = "#8a93a6";
    ctx.font = "10px ui-monospace, monospace";
    ctx.fillText(max.toPrecision(3), 4, pad.top + 8);
    ctx.fillText(min.toPrecision(3), 4, height - pad.bottom);
    ctx.fillText("1", pad.left, height - 5);
    ctx.fillText(String(maxTick), width - pad.right - 24, height - 5);

    kept.forEach(([, points], index) => {
      ctx.strokeStyle = CHART_COLORS[index % CHART_COLORS.length];
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      points.forEach(([t, value], i) => {
        const x = xOf(t);
        const y = yOf(value);
        if (i === 0) ctx.moveTo(x, y);
        else {
          ctx.lineTo(x, yOf(points[i - 1][1])); // steps-post
          ctx.lineTo(x, y);
        }
      });
      ctx.stroke();
    });

    // The shared-tick cursor.
    const cursorX = xOf(tick);
    ctx.strokeStyle = "#e8a33d";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cursorX, pad.top);
    ctx.lineTo(cursorX, height - pad.bottom);
    ctx.stroke();
  }, [kept, tick, maxTick]);

  return (
    <div className="metric-chart">
      <h4>{name}</h4>
      <canvas ref={canvas} style={{ width: "100%", height: 160 }} />
      <div className="legend">
        {kept.map(([key], index) => (
          <span key={key}>
            <span
              className="legend-swatch"
              style={{ background: CHART_COLORS[index % CHART_COLORS.length] }}
            />
            {key}
          </span>
        ))}
        {dropped > 0 && <span className="metric-note">… {dropped} more series not shown</span>}
      </div>
    </div>
  );
}
